import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import {
  ArrowRight, ShieldCheck, Lightning, FileText, SealCheck, Vault, ChartLineUp, Lock,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

const STEPS = [
  { icon: FileText, title: "Apply in minutes", desc: "Tell us about your business and how much capital you need. No paperwork to start." },
  { icon: ChartLineUp, title: "Get a tailored offer", desc: "Our team structures a line of credit with clear terms and sends you a secure offer link." },
  { icon: SealCheck, title: "Review, accept, fund", desc: "E-sign your offer, upload documents securely, and get funded — often within 24 hours." },
];

export default function Landing() {
  const [brand, setBrand] = useState({ company_name: "Business Line OC", accent_color: "#10B981" });
  useEffect(() => { api.get("/settings").then((r) => setBrand((p) => ({ ...p, ...r.data, company_name: r.data.company_name || p.company_name }))).catch(() => {}); }, []);
  const logoUrl = brand.logo_path ? `${api.defaults.baseURL}/branding/logo?v=${brand.logo_path}` : null;

  return (
    <div className="theme-admin min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-50" aria-hidden />
      <div className="absolute inset-0 mesh-glow" aria-hidden />

      {/* Nav */}
      <header className="relative z-20 max-w-6xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="h-9 w-9 rounded-lg object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Vault size={20} weight="duotone" className="text-primary" />
            </div>
          )}
          <span className="font-display font-bold">{brand.company_name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="team-login-link" className="text-sm text-muted-foreground hover:text-foreground px-3 py-2 transition-colors">
            Team Login
          </Link>
          <Link to="/apply">
            <Button data-testid="nav-apply-button" className="font-semibold">Apply Now</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 text-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/[0.08] px-3 py-1 text-xs font-semibold text-primary">
            <Lightning size={13} weight="fill" /> Funding in as little as 24 hours
          </div>
          <h1 className="font-display font-bold tracking-tighter mt-6 mx-auto max-w-3xl" style={{ fontSize: "clamp(2.5rem, 7vw, 4.75rem)", lineHeight: 1.02 }}>
            Business capital,<br />without the runaround.
          </h1>
          <p className="text-muted-foreground text-base md:text-lg mt-6 max-w-xl mx-auto">
            Flexible lines of credit from $50K to $500K for growing businesses. Transparent terms, a secure approval portal, and a team that moves fast.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/apply" className="w-full sm:w-auto">
              <Button data-testid="hero-apply-button" className="w-full sm:w-auto h-12 px-7 font-semibold text-base group">
                Apply for a Line of Credit
                <ArrowRight size={18} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
            <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Lock size={13} /> No impact to your credit to apply</span>
          </div>
        </motion.div>

        {/* Stat row */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="mt-16 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
          {[
            { v: 500, prefix: "$", suffix: "K", label: "Max credit line" },
            { v: 24, suffix: "h", label: "To funding" },
            { v: 100, suffix: "%", label: "Bank-grade security" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card card-inner-glow p-5">
              <div className="num text-2xl md:text-3xl font-bold text-primary">
                {s.prefix}<CountUp end={s.v} duration={1.4} />{s.suffix}
              </div>
              <div className="text-[11px] md:text-xs text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* How it works */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 pb-24">
        <div className="text-center mb-12">
          <div className="overline text-primary">How it works</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Three steps to funded</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-2xl border border-border bg-card card-inner-glow p-6"
            >
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 rounded-xl bg-primary/12 flex items-center justify-center">
                  <s.icon size={22} weight="duotone" className="text-primary" />
                </div>
                <span className="num text-4xl font-bold text-border">{i + 1}</span>
              </div>
              <h3 className="font-display text-xl font-semibold mt-5">{s.title}</h3>
              <p className="text-muted-foreground text-sm mt-2">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 pb-24">
        <div className="rounded-3xl border border-primary/25 bg-primary/[0.06] card-inner-glow p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 mesh-glow" aria-hidden />
          <div className="relative">
            <ShieldCheck size={34} weight="duotone" className="text-primary mx-auto" />
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-5">Ready when you are</h2>
            <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Apply today and a member of our team will prepare your offer. It only takes a few minutes to get started.</p>
            <Link to="/apply">
              <Button data-testid="cta-apply-button" className="mt-7 h-12 px-8 font-semibold text-base">Start Your Application <ArrowRight size={18} className="ml-1.5" /></Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} {brand.company_name}. All rights reserved.</span>
          <Link to="/login" className="hover:text-foreground transition-colors">Team Login</Link>
        </div>
      </footer>
    </div>
  );
}
